import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '../ui/components';
import { Icon } from '../ui/icons';
import { auth as authApi } from '../lib/api';
import { useT } from '../i18n';
import './LoginPage.css';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = useT();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError(t('Password must be at least 8 characters.')); return; }
    if (password !== confirm) { setError(t('Passwords do not match.')); return; }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      setError(err?.message || t('Could not reset the password.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ps-scope pp-login">
      <aside className="pp-login__brand">
        <div className="pp-login__logo">
          <span className="pp-login__logo-mark">P</span>
          <span>Prostay Procurement</span>
        </div>
        <div className="pp-login__pitch">
          <h1 className="pp-login__headline">{t('Choose a new password')}</h1>
          <p className="pp-login__sub">{t("You'll be signed out everywhere else, then you can sign back in with the new password.")}</p>
        </div>
      </aside>

      <section className="pp-login__panel">
        <div className="pp-login__card">
          {done ? (
            <>
              <h2 className="pp-login__title">{t('Password updated')}</h2>
              <p className="pp-login__desc">{t('You can sign in with your new password now. Redirecting…')}</p>
            </>
          ) : !token ? (
            <>
              <h2 className="pp-login__title">{t('Reset link invalid')}</h2>
              <p className="pp-login__desc">{t('This reset link is invalid or incomplete. Request a new one.')}</p>
              <Button variant="secondary" size="lg" fullWidth onClick={() => navigate('/forgot-password')}>
                {t('Request a new link')}
              </Button>
            </>
          ) : (
            <>
              <h2 className="pp-login__title">{t('Choose a new password')}</h2>
              <p className="pp-login__desc">{t('Almost there — pick something strong and memorable.')}</p>
              <form className="pp-login__form" onSubmit={submit}>
                {error && (
                  <div className="pp-login__error" role="alert">
                    <Icon.CircleAlert size={16} />
                    <span>{error}</span>
                  </div>
                )}
                <Input
                  label={t('New password')}
                  type="password"
                  autoFocus
                  autoComplete="new-password"
                  placeholder={t('At least 8 characters')}
                  leadingIcon={<Icon.Lock size={16} />}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Input
                  label={t('Confirm new password')}
                  type="password"
                  autoComplete="new-password"
                  leadingIcon={<Icon.Lock size={16} />}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
                  {t('Set new password')}
                </Button>
              </form>
              <p className="pp-login__foot">
                <Link to="/forgot-password" className="pp-login__link">{t('Link expired? Request a new one')}</Link>
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
